import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './navbar.css';
import cael from '../Assets/cael-logo.png';
import { useAuthStore } from '../../stores/authStore';
import { useCart } from '../../hooks/useCart';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import SearchAutocomplete from '../SearchAutocomplete/SearchAutocomplete';
import { Search, ShoppingCart, User, ChevronDown, X, LogOut, Store, MessageCircle } from 'lucide-react';

const Navbar = () => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const userMenuRef = useRef(null);
  
  // Auth state
  const { isAuthenticated, user, logout } = useAuthStore();
  
  // Cart data
  const { totalItems } = useCart();
  
  // Translations
  const { t } = useTranslation('nav');

  // Focus search input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchOpen && !e.target.closest('.search-container') && !e.target.closest('.search-toggle')) {
        setSearchOpen(false);
      }
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [searchOpen, userMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Use 'q' parameter to match SearchPage expectations
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="navbar-wrapper">
      {/* Top Promo Strip - Hidden, now using AdBanner component */}

      {/* Main Header */}
      <div className="main-header">
        <div className="header-container">
          {/* Logo */}
          <Link to="/" className="logo">
            <img src={cael} alt="St Cael Vog" />
          </Link>

          {/* Desktop Search Bar - Always visible on desktop */}
          <div className="search-wrapper">
            <form className={`search-container ${searchOpen ? 'mobile-open' : ''}`} onSubmit={handleSearch}>
              <Search className="search-icon" size={20} />
              <input 
                ref={searchInputRef}
                type="text" 
                placeholder={t('search')} 
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="search-btn">{t('search', { ns: 'common' })}</button>
              {/* Close button for mobile */}
              <button type="button" className="search-close" onClick={() => setSearchOpen(false)}>
                <X size={20} />
              </button>
            </form>
            {/* Search Autocomplete */}
            {searchOpen && (
              <SearchAutocomplete
                query={searchQuery}
                onQueryChange={setSearchQuery}
                onClose={() => setSearchOpen(false)}
                onSearch={handleSearch}
              />
            )}
          </div>

          {/* Right Actions */}
          <div className="header-actions">
            {/* Boutique/Shop Icon - Direct to search page */}
            <Link to="/search" className="boutique-link" title="Boutique">
              <Store size={22} />
            </Link>

            {/* Mobile Search Toggle */}
            <button className="search-toggle" onClick={() => setSearchOpen(true)}>
              <Search size={22} />
            </button>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Notifications - Only show if authenticated */}
            {isAuthenticated && <NotificationDropdown />}

            {/* Cart */}
            <Link to="/carte" className="cart-link">
              <ShoppingCart size={24} />
              {totalItems > 0 && (
                <span className="cart-badge">{totalItems}</span>
              )}
            </Link>

            {/* User Button */}
            {isAuthenticated ? (
              <div className="user-menu-container" ref={userMenuRef}>
                <button 
                  className="user-btn"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <User size={16} />
                  <span className="user-name">
                    {user?.firstName || user?.name?.split(' ')[0] || user?.lastName || 'Account'}
                  </span>
                  <ChevronDown size={12} />
                </button>
                
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <Link to="/profile" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <User size={16} />
                      {t('profile')}
                    </Link>
                    <Link to="/conversations" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <MessageCircle size={16} />
                      {t('messages')}
                    </Link>
                    <Link to="/profile?tab=orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <ShoppingCart size={16} />
                      {t('orders')}
                    </Link>
                    <button className="dropdown-item logout" onClick={handleLogout}>
                      <LogOut size={16} />
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="signin-btn">
                <User size={18} />
                <span>{t('login')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
